import isPlainObject from 'lodash/isPlainObject';
import * as actionTypes from 'actions/actionTypes';
import { getInvoiceAmount } from 'utils/helpers';

const initialState = {
  isLoading: false,
  contracts: {},
  transactions: {},
  invoices: {},
  lastInvoice: {},
  isWithdrawalLoading: false,
  isWithdrawalSuccess: false,
  isWithdrawalError: false,
  isUpgradeHighlighted: false,
  profileFormsState: {
    activeFormId: null,
    isSaving: false,
  },
  dueDateRange: {},
  isUpgradeSuccess: false,
  changeDueDateNewMMP: null,
  creditApplicationState: null,
  isNativeAdvertisingEnabled: COUNTRY === 'CZ' || COUNTRY === 'AU',
  statements: {
    [new Date().getFullYear()]: [],
  },
  isApplyIncreaseLoading: false,
  shouldUpdateModalOpen: true,
};

const getInvoicesAmount = (invoices) => {
  let amount = 0;

  if (!invoices.length) {
    return amount;
  }

  const list = invoices.map((item) => getInvoiceAmount(item));

  if (!list.length) {
    return amount;
  }

  amount = list.reduce((accumulator, currentValue) => accumulator + currentValue);

  return amount;
};

export default function reducer(state = initialState, action) {
  if (DEBUG) {
    console.log('reducers action: ', action);
  }

  switch (action.type) {
    case actionTypes.LOGOUT_SUCCESS: {
      return { ...initialState };
    }

    case actionTypes.GET_CONTRACT_DATA_SUCCESS: {
      const { skipExtraServices } = action.data;
      const contractData = action.result[0].data;
      const outstanding = action.result[1].data;
      let drawBlockedReason = contractData.drawBlockedReason;
      const canFreeDraw = !drawBlockedReason && contractData.availableLimit > 0;
      if (COUNTRY === 'CZ') {
        if (!drawBlockedReason && contractData.availableLimit === 0) {
          drawBlockedReason = 'NO_FREEDRAW';
        }
        if (contractData.lateAmount > 0 && (!drawBlockedReason || drawBlockedReason === 'NO_FREEDRAW')) {
          drawBlockedReason = 'UNPAID_INVOICES';
        }
      }
      let contractExtraServices;
      let upgrade = {};
      let isUpgradeHighlighted = false;
      if (!skipExtraServices) {
        const extraServicesReq = action.result[2];
        if (extraServicesReq.config.contractExtraServices) {
          contractExtraServices = extraServicesReq.config.contractExtraServices;
          const contractUpgrade = extraServicesReq.data;
          if (contractUpgrade.length && contractExtraServices.UPGRADE_CREDITLINE.isAvailable) {
            contractUpgrade.sort((a, b) => (b.product.principal - a.product.principal));
            upgrade = {
              ...contractUpgrade[0],
            };
            isUpgradeHighlighted = true;
          }
        } else {
          contractExtraServices = extraServicesReq.data;
        }
      }
      const contract = state.contracts[contractData.id] || {};
      const nextState = {
        ...state,
        isLoading: false,
        contracts: {
          ...state.contracts,
          [contractData.id]: {
            ...contract,
            ...contractData,
            drawBlockedReason,
            ...contractExtraServices,
            outstanding,
            upgrade,
            canFreeDraw,
            prediction: {
              ...contract.prediction,
            },
          },
        },
        isUpgradeHighlighted,
        isContractDataLoaded: true,
      };
      if (COUNTRY === 'FI' || COUNTRY === 'CZ') {
        if (contractData.drawBlockedReason === 'PENDING_CL_UPGRADE') {
          nextState.creditApplicationState = contractData.drawBlockedReason;
          nextState.waitingForNewContract = true;
        } else if (nextState.creditApplicationState) {
          delete nextState.creditApplicationState;
          nextState.waitingForNewContract = false;
        }
      }
      if (COUNTRY === 'CZ') {
        const mmp = action.result[action.result.length - 1].data.amount;
        const nextInvoice = action.result[3].data.dueDate;
        nextState.contracts[contractData.id].mmp = mmp;
        nextState.contracts[contractData.id].nextDueDate = nextInvoice;
      }
      return nextState;
    }

    case actionTypes.GET_PAYMENT_HOLIDAY_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId];
      return {
        ...state,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...contract,
            prediction: {
              ...contract.prediction,
              PAYMENT_HOLIDAY: {
                ...action.result.data,
              },
            },
          },
        },
      };
    }

    case actionTypes.GET_ACCOUNT_STATEMENT_SUCCESS: {
      const { data, config: { contractId } } = action.result;
      return {
        ...state,
        transactions: {
          ...state.transactions,
          [contractId]: data.transactions.reverse(),
        },
      };
    }

    case actionTypes.GET_ACCOUNT_STATEMENT_DETAILED_SUCCESS: {
      const { data, config: { contractId } } = action.result;
      return {
        ...state,
        transactions: {
          ...state.transactions,
          [contractId]: {
            totalDebt: data.totalBalance,
            endDate: action.data.to,
            transactionsData: data.transactions.reverse(),
          },
        },
      };
    }

    case actionTypes.GET_OPEN_INVOICE_SUCCESS: {
      const { data, config: { contractId } } = action.result;
      return {
        ...state,
        invoices: {
          ...state.invoices,
          [contractId]: {
            ...state.invoices[contractId],
            openInvoice: isPlainObject(data) ? data : {},
          },
        },
      };
    }

    case actionTypes.GET_INVOICES_SUCCESS: {
      const { data, config: { contractId } } = action.result;

      const openInvoices = data.filter((item) =>
        item.isLate === false && !item.closeDate && !(item.dueDays < 0));
      const paidInvoices = data.filter((item) => item.closeDate && item.closeType === 'PAID');
      const unpaidInvoices = data.filter((item) => !item.closeDate && item.dueDays < 0);

      const unpaidAmount = getInvoicesAmount(unpaidInvoices);
      const totalAmount = unpaidAmount + getInvoicesAmount(openInvoices);

      const nextState = {
        ...state,
        invoices: {
          ...state.invoices,
          [contractId]: {
            ...state.invoices[contractId],
            paidInvoices,
            openInvoices,
            unpaidInvoices,
            unpaidAmount,
            totalAmount,
          },
        },
      };
      if (COUNTRY === 'CZ') {
        const drawBlockedReason = state.contracts[contractId].drawBlockedReason;
        if (unpaidAmount > 0 && (!drawBlockedReason || drawBlockedReason === 'NO_FREEDRAW')) {
          nextState.contracts[contractId].drawBlockedReason = 'UNPAID_INVOICES';
        }
      }
      return nextState;
    }

    case actionTypes.GET_LAST_INVOICE_SUCCESS: {
      const { data, config: { contractId } } = action.result;
      const isUnpaidInvoice = !data.closeDate && data.dueDays < 0;
      const isOpenInvoice = data.isLate === false && !data.closeDate && !(data.dueDays < 0);
      const nextState = {
        ...state,
        lastInvoice: {
          ...state.lastInvoice,
          [contractId]: {
            ...state.lastInvoice[contractId],
            ...data,
            isUnpaidInvoice: isUnpaidInvoice || isOpenInvoice,
            isPaidInvoice: data.closeDate && data.closeType === 'PAID',
          },
        },
      };
      return nextState;
    }

    case actionTypes.GET_MMPS_SUCCESS: {
      let { data } = action.result;
      const { config: { contractId } } = action.result;
      if (COUNTRY === 'AU') {
        // AU slider has a step of 50, take datapoints in increments of 50 & the maximum MMP
        data = data.filter(
          (item, index) => !(item.sum % 5000) || index === data.length - 1
        );
      }
      return {
        ...state,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...state.contracts[contractId],
            mmps: data,
          },
        },
      };
    }

    case actionTypes.GET_MMP_BY_NEW_DUE_DATE_SUCCESS: {
      return {
        ...state,
        changeDueDateNewMMP: action.result.data.mmp,
      };
    }

    case actionTypes.PUT_WITHDRAWAL: {
      return {
        ...state,
        isWithdrawalSuccess: false,
        isWithdrawalError: false,
        isWithdrawalLoading: true,
      };
    }

    case actionTypes.PUT_WITHDRAWAL_SUCCESS: {
      return {
        ...state,
        isWithdrawalLoading: false,
        isWithdrawalSuccess: true,
      };
    }

    case actionTypes.PUT_WITHDRAWAL_FAIL: {
      const { data = {} } = action.error;
      const { statusCode } = data;

      return {
        ...state,
        isWithdrawalLoading: false,
        isWithdrawalError: true,
        withdrawalStatusCode: statusCode,
      };
    }

    case actionTypes.PUT_NEW_CREDIT_LINE_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        isUpgradeSuccess: true,
        waitingForNewContract: true,
        creditApplicationState: 'PENDING_CL_UPGRADE',
      };
    }

    case actionTypes.GET_BANK_NAME_SUCCESS: {
      return {
        ...state,
        customerBankName: action.result.data.bankName,
      };
    }

    case actionTypes.GET_DUE_DATE_RANGE_SUCCESS: {
      return {
        ...state,
        dueDateRange: action.result.data,
      };
    }

    case actionTypes.SET_UPGRADE_HIGHLIGHT_OFF: {
      return {
        ...state,
        isUpgradeHighlighted: false,
      };
    }

    case actionTypes.START_PROFILE_EDITING: {
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          activeFormId: action.data.formId,
        },
      };
    }

    case actionTypes.CANCEL_PROFILE_EDITING: {
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          activeFormId: null,
        },
      };
    }

    case actionTypes.START_ONE_CENT_IDENTIFICATION:
    case actionTypes.SAVE_MARKETING_CONSENTS:
    case actionTypes.SAVE_BANK_ACCOUNT:
    case actionTypes.SAVE_PASSWORD:
    case actionTypes.SAVE_CONTACT_INFO:
    case actionTypes.UPDATE_CONTACT_INFO:
    case actionTypes.VERIFY_MSISDN: {
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          isSaving: true,
          exception: null,
        },
      };
    }

    case actionTypes.START_ONE_CENT_IDENTIFICATION_SUCCESS:
    case actionTypes.START_ONE_CENT_IDENTIFICATION_FAIL:
    case actionTypes.SAVE_MARKETING_CONSENTS_SUCCESS:
    case actionTypes.SAVE_BANK_ACCOUNT_SUCCESS:
    case actionTypes.SAVE_PASSWORD_SUCCESS:
    case actionTypes.SAVE_CONTACT_INFO_SUCCESS:
    case actionTypes.UPDATE_CONTACT_INFO_SUCCESS:
    case actionTypes.VERIFY_MSISDN_SUCCESS: {
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          activeFormId: null,
          isSaving: false,
        },
      };
    }

    case actionTypes.SAVE_MARKETING_CONSENTS_FAIL:
    case actionTypes.SAVE_BANK_ACCOUNT_FAIL:
    case actionTypes.SAVE_PASSWORD_FAIL:
    case actionTypes.SAVE_CONTACT_INFO_FAIL:
    case actionTypes.UPDATE_CONTACT_INFO_FAIL:
    case actionTypes.VERIFY_MSISDN_FAIL: {
      let exception = null;
      if (action.error.data && action.error.data.errCode) {
        exception = action.error.data.errCode;
      }
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          isSaving: false,
          exception,
        },
      };
    }

    case actionTypes.CANCEL_UPDATE_DATA: {
      return {
        ...state,
        shouldUpdateModalOpen: false,
      }
    }

    case actionTypes.SET_UPGRADE_SUCCESS_OFF: {
      return {
        ...state,
        isUpgradeSuccess: false,
      };
    }

    case actionTypes.GET_CREDIT_APPLICATION_SUCCESS: {
      const { data } = action.result;
      if (COUNTRY === 'FI') {
        const { documentRequirements } = data;
        if (documentRequirements && documentRequirements.length > 0) {
          return state;
        }
      }
      return {
        ...state,
        creditApplicationState: data.state,
      };
    }
    case actionTypes.GET_CREDIT_APPLICATION_FAILURE: {
      if (action.error.status === 404) {
        return {
          ...state,
          creditApplicationState: 'ABANDONED',
        };
      }
      return state;
    }

    case actionTypes.GET_STATEMENT_OF_ACCOUNT_SUCCESS: {
      const nextState = {
        ...state,
        areStatementsLoaded: action.data.finalCall,
      };
      action.result.forEach((result) => {
        const { data, config: { year } } = result;
        if (data.items.length) {
          nextState.statements[year] = data.items.sort((a, b) => b.id - a.id);
        }
      });

      return nextState;
    }

    case actionTypes.GET_CONTRACT_FREEZE_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId] || {};
      return {
        ...state,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...contract,
            CONTRACT_FREEZE: {
              ...contract.CONTRACT_FREEZE,
              ...action.result.data,
            },
          },
        },
      };
    }

    case actionTypes.PUT_NEW_CREDIT_LINE:
    case actionTypes.TOGGLE_DUE_DATE_REMINDER:
    case actionTypes.CHANGE_DUE_DATE:
    case actionTypes.TAKE_PAYMENT_HOLIDAY:
    case actionTypes.GET_CONTRACT_DATA:
    case actionTypes.PUT_KONTOMATIK_SESSION:
    case actionTypes.POST_UPGRADE_APPLICATION:
    case actionTypes.CONFIRM_UPGRADE_APPLICATION:
      return {
        ...state,
        isLoading: true,
      };

    case actionTypes.PUT_NEW_CREDIT_LINE_FAIL:
    case actionTypes.TOGGLE_DUE_DATE_REMINDER_FAIL:
    case actionTypes.CHANGE_DUE_DATE_FAIL:
    case actionTypes.TAKE_PAYMENT_HOLIDAY_FAIL:
    case actionTypes.TOGGLE_DUE_DATE_REMINDER_SUCCESS:
    case actionTypes.CHANGE_DUE_DATE_SUCCESS:
    case actionTypes.TAKE_PAYMENT_HOLIDAY_SUCCESS:
    case actionTypes.GET_CONTRACT_DATA_FAIL:
    case actionTypes.PUT_KONTOMATIK_SESSION_FAIL:
    case actionTypes.POST_UPGRADE_APPLICATION_FAIL:
    case actionTypes.CONFIRM_UPGRADE_APPLICATION_FAIL:
      return {
        ...state,
        isLoading: false,
      };

    case actionTypes.PUT_KONTOMATIK_SESSION_SUCCESS:
      return {
        ...state,
        profileFormsState: {
          ...state.profileFormsState,
          activeFormId: null,
        },
        isLoading: false,
      };

    case actionTypes.GET_CREDIT_APPLICATION_STATUS_SUCCESS:
      return {
        ...state,
        waitingForNewContract: true,
      };

    case actionTypes.GET_PRODUCT_PRINCIPAL_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId];
      return {
        ...state,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...contract,
            productPrincipal: action.result.data.principal,
          },
        },
      };
    }

    case actionTypes.GET_PRINCIPAL_BALANCE_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId];
      return {
        ...state,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...contract,
            principalBalance: action.result.data.principal,
          },
        },
      };
    }

    case actionTypes.GET_UPGRADE_ELIGIBILITY_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId];
      const { eligible, totalAmount, increaseType } = action.result.data;
      if (eligible) {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            [contractId]: {
              ...contract,
              upgrade: {
                increaseType,
                product: {
                  principal: totalAmount,
                },
              },
            },
          },
        };
      }
      return state;
    }

    case actionTypes.GET_UPSELL_ELIGIBILITY_SUCCESS: {
      const { contractId } = action.data;
      const contract = state.contracts[contractId];
      const { eligible, receivedAmount, increaseType } = action.result.data;
      if (eligible) {
        return {
          ...state,
          contracts: {
            ...state.contracts,
            [contractId]: {
              ...contract,
              upgrade: {
                increaseType,
                product: {
                  principal: receivedAmount,
                },
              },
            },
          },
        };
      }
      return state;
    }

    case actionTypes.APPLY_INCREASE:
      return {
        ...state,
        isApplyIncreaseLoading: true,
      };

    case actionTypes.APPLY_INCREASE_SUCCESS:
    case actionTypes.APPLY_INCREASE_FAIL:
      return {
        ...state,
        isApplyIncreaseLoading: false,
      };

    case actionTypes.GET_UPGRADE_APPLICATION: {
      const { contractId } = action.data;
      const contract = {
        ...state.contracts[contractId],
        upgradeApplicationChecked: false,
      };
      delete contract.upgradeApplication;
      return {
        ...state,
        isLoading: false,
        contracts: {
          ...state.contracts,
          [contractId]: contract,
        },
      };
    }

    case actionTypes.GET_UPGRADE_APPLICATION_SUCCESS:
    case actionTypes.POST_UPGRADE_APPLICATION_SUCCESS: {
      const { contractId } = action.data;
      return {
        ...state,
        isLoading: false,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...state.contracts[contractId],
            upgradeApplication: action.result.data,
            upgradeApplicationChecked: true,
          },
        },
      };
    }

    case actionTypes.GET_UPGRADE_APPLICATION_FAIL: {
      const { contractId } = action.data;
      return {
        ...state,
        isLoading: false,
        contracts: {
          ...state.contracts,
          [contractId]: {
            ...state.contracts[contractId],
            upgradeApplicationChecked: true,
          },
        },
      };
    }

    case actionTypes.CONFIRM_UPGRADE_APPLICATION_SUCCESS: {
      const { upgradeApplication } = state.contracts[action.data.contractId];
      const upgradeFinished = (
        upgradeApplication.state === 'CUSTOMER_APPROVAL'
        || upgradeApplication.documentRequirements.length === 0
      );
      return {
        ...state,
        isLoading: false,
        isUpgradeSuccess: upgradeFinished,
      };
    }

    default:
      return state;
  }
}
